import axios from 'axios';

// 公開ページは 401 でログインへ飛ばされないよう素の axios を使う
const pub = axios.create();
const API = `${process.env.REACT_APP_BACKEND_URL}/api/planner`;

// 画面とサイドバーが同じものを同時に取りに行くため、同じ GET が二重・三重に出ていた。
// 進行中の取得があれば相乗りし、直後の再取得は短時間だけ使い回す。
const inflight = new Map();
const cache = new Map();
const DEDUPE_MS = 5000;

const shared = (key, fetcher) => {
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < DEDUPE_MS) return Promise.resolve(hit.data);
  if (inflight.has(key)) return inflight.get(key);
  const p = fetcher()
    .then((data) => { cache.set(key, { at: Date.now(), data }); return data; })
    .finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
};

export const plannerApi = {
  departments: () => shared('departments', () => axios.get(`${API}/departments`).then((r) => r.data)),
  people: (params) => axios.get(`${API}/people`, { params }).then((r) => r.data),
  // projects
  projects: (params) => axios.get(`${API}/projects`, { params }).then((r) => r.data),
  project: (id) => axios.get(`${API}/projects/${id}`).then((r) => r.data),
  createProject: (b) => axios.post(`${API}/projects`, b).then((r) => r.data),
  updateProject: (id, b) => axios.patch(`${API}/projects/${id}`, b).then((r) => r.data),
  archiveProject: (id, b) => axios.post(`${API}/projects/${id}/archive`, b || {}).then((r) => r.data),
  deleteProject: (id) => axios.delete(`${API}/projects/${id}`).then((r) => r.data),
  // tasks
  tasks: (params) => axios.get(`${API}/tasks`, { params }).then((r) => r.data),
  createTask: (b) => axios.post(`${API}/tasks`, b).then((r) => r.data),
  updateTask: (id, b) => axios.patch(`${API}/tasks/${id}`, b).then((r) => r.data),
  deleteTask: (id) => axios.delete(`${API}/tasks/${id}`).then((r) => r.data),
  // dashboard / alerts
  dashboard: (params) => axios.get(`${API}/dashboard`, { params }).then((r) => r.data),
  alerts: () => shared('alerts', () => axios.get(`${API}/alerts`).then((r) => r.data)),
  myTasks: (params) => axios.get(`${API}/my-tasks`, { params }).then((r) => r.data),
  myProjects: (params) => axios.get(`${API}/my-projects`, { params }).then((r) => r.data),
  // 週間ダイジェスト
  digest: (params) => axios.get(`${API}/digest`, { params }).then((r) => r.data),
  digestSettings: () => axios.get(`${API}/digest/settings`).then((r) => r.data),
  saveDigestSettings: (b) => axios.put(`${API}/digest/settings`, b).then((r) => r.data),
  digestRecipients: () => axios.get(`${API}/digest/recipients`).then((r) => r.data),
  sendDigest: (b) => axios.post(`${API}/digest/send`, b).then((r) => r.data),
  // gantt public share
  getShare: (kind, id) => axios.get(`${API}/share/${kind}/${id}`).then((r) => r.data),
  createShare: (kind, id, body = {}) =>
    axios.post(`${API}/share/${kind}/${id}`, body).then((r) => r.data),
  listShares: () => axios.get(`${API}/shares`).then((r) => r.data),
  revokeShare: (kind, id) => axios.delete(`${API}/share/${kind}/${id}`).then((r) => r.data),
  sharedGantt: (token, session) =>
    pub.get(`${API}/shared/${token}`, { params: session ? { session } : {} }).then((r) => r.data),
  unlockShare: (token, password) =>
    pub.post(`${API}/shared/${token}/unlock`, { password }).then((r) => r.data),
  // 過去関連作業 (Archived Related Work)
  archivedContext: (pid, params) => axios.get(`${API}/projects/${pid}/archived-context`, { params }).then((r) => r.data),
  archivedByDept: (deptId) => axios.get(`${API}/archived-context/by-department/${deptId}`).then((r) => r.data),
  archiveReview: (params) => axios.get(`${API}/archived-context/review`, { params }).then((r) => r.data),
  archiveApprove: (id) => axios.post(`${API}/archived-context/${id}/approve`).then((r) => r.data),
  archiveReject: (id) => axios.post(`${API}/archived-context/${id}/reject`).then((r) => r.data),
  archivePatch: (id, b) => axios.patch(`${API}/archived-context/${id}`, b).then((r) => r.data),
  archiveBulkApprove: (ids) => axios.post(`${API}/archived-context/bulk-approve`, { ids }).then((r) => r.data),
  archiveDryRun: (fd) => axios.post(`${API}/archive-import/dry-run`, fd).then((r) => r.data),
  archiveCommit: (fd) => axios.post(`${API}/archive-import/commit`, fd).then((r) => r.data),
  archiveRollback: (batchId) => axios.post(`${API}/archive-import/rollback`, { batch_id: batchId }).then((r) => r.data),
  archiveBatches: () => axios.get(`${API}/archive-import/batches`).then((r) => r.data),
  // archived old projects (旧プロジェクト保管庫)
  archiveExtractOldProject: (id) => axios.post(`${API}/archived-context/${id}/extract-old-project`).then((r) => r.data),
  archiveCreateOldProject: (id, b) => axios.post(`${API}/archived-context/${id}/create-old-project`, b).then((r) => r.data),
  oldProjects: (params) => axios.get(`${API}/archived-old-projects`, { params }).then((r) => r.data),
  oldProject: (id) => axios.get(`${API}/archived-old-projects/${id}`).then((r) => r.data),
  updateOldProject: (id, b) => axios.patch(`${API}/archived-old-projects/${id}`, b).then((r) => r.data),
  deleteOldProject: (id) => axios.delete(`${API}/archived-old-projects/${id}`).then((r) => r.data),
};
